import { HotelSummary, TransferPartner } from "@/generated/search.openapi";
import {
  calculateNumberOfNights,
  cn,
  convertJsonToUrlParams,
  formatCurrency,
  formatNumber,
  getCountryName,
  getDateFormat,
  getDisplayNightlyRate,
  getDisplayTotalCost,
  getFilterRateKey,
  getRating,
  getTransferPartners,
  screenInnerHeight,
} from "@/lib/utils";
import React, {
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
} from "react";
import { useInView } from "react-intersection-observer";
import HotelCardSkeleton from "./HotelCardSkeleton";
import { format } from "date-fns";
import { getApproximatePercentile } from "@/app/(app)/[bookingType]/search/[searchId]/helper";
import ArrowCircleLeft from "@/components/ui/svgComponents/ArrowCircleLeft";
import ArrowCircleRight from "@/components/ui/svgComponents/ArrowCircleRight";
import { X } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import "@/styles/hotelMapCard.css";
import Star from "@/components/ui/svgComponents/Star";
import Calendar from "@/components/ui/svgComponents/Calendar";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import {
  FilterOption,
  useSearch,
} from "@/app/(app)/[bookingType]/search/context/searchProvider";
import { LoyaltyProgramEnum, Tag } from "@/generated/email_parser.openapi";
import { FreeNightMatch } from "@/generated/search.openapi";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/toolTip";
import { loyaltyAccountsColorMapping } from "@/lib/loyaltyUtils";
import { encodeText } from "@/lib/textUtils";
import { SortOption } from "@/components/section/explore/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import FavoriteButton from "./FavoriteButton";

type Props = {
  hotel: HotelSummary;
  setHoveredHotelId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  numberOfAdults?: number;
  childrenAges?: number[];
  className?: HTMLAttributes<HTMLDivElement>["className"];
  innerClassName?: HTMLAttributes<HTMLDivElement>["className"];
  closeCard?: () => void;
  showDate?: boolean;
  isTallerImage?: boolean;
  disableOnClick?: boolean;
  availableTransferPartners?: TransferPartner[];
  showTags?: boolean;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
  showDateRangeTag?: boolean;
  isMapView?: boolean;
  sortBy?: SortOption;
  noPadding?: boolean;
  reserveLoyaltyTagSpace?: boolean;
  onImpression?: (
    propertyId: number,
    score?: number | null,
    position?: number
  ) => void;
  onClickTrack?: (
    propertyId: number,
    score?: number | null,
    position?: number
  ) => void;
  searchSessionId?: string;
  index?: number;
  isComparisonMode?: boolean;
  isSelectedForComparison?: boolean;
  onComparisonToggle?: (id: number) => void;
  // When this card is rendered into a detached React tree (e.g. the map
  // popover, via createRoot) it can't read SearchContext. Callers in that
  // situation should pass the live filters here so the displayed rate
  // matches the list card. See GON-5654.
  filtersOverride?: FilterOption;
  // GON-6020 content-first: when this card has no rate yet, what to show in the
  // price area. "loading" → spinner ("Checking rates…") while rates stream;
  // "unavailable" → "No rates" once the search is complete. Undefined keeps the
  // legacy "sold out" message (non-content-first). "view-rates" is for
  // content-only cards that have not looked up rates yet.
  noRateAffordance?: "loading" | "unavailable" | "view-rates";
  // FNC cap-vs-rate match for this property when the user holds a covering
  // credit (GON-5965 Phase 4a). Parent batches the lookup across the visible
  // page and passes the result per-card; absent means no badge.
  freeNightMatch?: FreeNightMatch;
  favoriteState?: boolean;
  favoritePending?: boolean;
  onFavoriteToggle?: () => void;
  navigationLabel?: string;
};

const HotelCard: React.FC<Props> = ({
  hotel,
  className,
  innerClassName,
  setHoveredHotelId,
  closeCard,
  showDate,
  isTallerImage,
  disableOnClick,
  numberOfAdults,
  childrenAges,
  availableTransferPartners,
  showTags,
  onClick: _onClick,
  showDateRangeTag,
  isMapView,
  sortBy,
  noPadding,
  reserveLoyaltyTagSpace = false,
  onImpression,
  onClickTrack,
  searchSessionId,
  index,
  isComparisonMode,
  isSelectedForComparison,
  onComparisonToggle,
  filtersOverride,
  noRateAffordance,
  freeNightMatch,
  favoriteState,
  favoritePending = false,
  onFavoriteToggle,
  navigationLabel,
}) => {
  const { inView, ref } = useInView({
    initialInView: false,
    fallbackInView: false,
    triggerOnce: true,
    rootMargin: "200px",
  });

  const checkinStr = hotel.checkinStr
    ? hotel.checkinStr
    : format(getDateFormat(hotel.checkin), "yyyy-MM-dd");
  const checkoutStr = hotel.checkoutStr
    ? hotel.checkoutStr
    : format(getDateFormat(hotel.checkout), "yyyy-MM-dd");

  const nights =
    checkinStr && checkoutStr
      ? calculateNumberOfNights(checkinStr, checkoutStr)
      : null;

  const { filters: contextFilters } = useSearch();
  const filters = filtersOverride ?? contextFilters;

  const filterRateKey = useMemo(
    () => getFilterRateKey(filters?.roomType, filters?.bedType),
    [filters?.roomType, filters?.bedType]
  );
  const activeFilterRate = filterRateKey
    ? hotel.lowestRatesByFilter?.[filterRateKey]
    : undefined;

  const forYouScore = hotel.recommendationSummary?.forYouScore;

  // Track impression when card scrolls into view
  useEffect(() => {
    if (inView && onImpression && hotel.vervotechPropertyId) {
      onImpression(hotel.vervotechPropertyId, forYouScore, index);
    }
  }, [inView, onImpression, hotel.vervotechPropertyId, forYouScore, index]);

  const onClick: React.MouseEventHandler<HTMLImageElement> = (e) => {
    e.stopPropagation();
    if (disableOnClick) return;
    if (onClickTrack && hotel.vervotechPropertyId) {
      onClickTrack(hotel.vervotechPropertyId, forYouScore, index);
    }
    window.open(
      `/hotel/details/${hotel.vervotechPropertyId}${convertJsonToUrlParams({
        numberOfAdults: numberOfAdults || 1,
        childrenAges: (childrenAges ?? []).join(","),
        checkin: checkinStr,
        checkout: checkoutStr,
        s: encodeText(window.location.pathname),
        ...(searchSessionId ? { sid: searchSessionId } : {}),
        ...(forYouScore != null ? { fys: forYouScore } : {}),
      })}`,
      "_blank"
    );
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      navigationLabel &&
      !disableOnClick &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };
  const images = useMemo(() => {
    if (inView) {
      const baseImages =
        hotel.images
          ?.filter((img) => {
            if (!img.url?.trim()) return false;
            if (filters?.selectedImageCategories === undefined) return true;
            return img.category === filters.selectedImageCategories;
          })
          .map((img) => img.url.trim()) ?? [];
      // Prepend filter rate room image (e.g. suite image) when a room type filter is active
      const activeFilterImage = activeFilterRate?.imageUrl?.trim();
      if (activeFilterImage) {
        return [
          activeFilterImage,
          ...baseImages.filter((url) => url !== activeFilterImage),
        ];
      }
      return baseImages;
    }
    return [];
  }, [
    filters?.selectedImageCategories,
    hotel.images,
    inView,
    activeFilterRate,
  ]);

  const imageHeight = useMemo(() => {
    if (screenInnerHeight < 768 && isMapView) {
      return "h-[130px]";
    }
    if (isMapView) {
      return "h-[210px]";
    }
    return isTallerImage ? "h-[275px]" : "h-[230px]";
  }, [isTallerImage, isMapView]);

  const imageGalleryItems: ReactImageGalleryItem[] = useMemo(() => {
    if (!images?.length) return [];
    return images.map((image) => ({
      original: image,
      lazyLoad: true,
      infinite: false,
      renderItem: (item) => (
        <img
          src={item.original}
          alt={item.originalAlt}
          className={cn(
            "w-full cursor-pointer rounded-lg object-cover",
            imageHeight
          )}
        />
      ),
    })) as ReactImageGalleryItem[];
  }, [images, imageHeight]);

  const approximatePercentile = getApproximatePercentile(hotel, true);

  const transferPartners = getTransferPartners(
    hotel.loyaltyProgram!,
    availableTransferPartners ?? []
  );

  const hotelTags = useMemo(() => {
    const hasPointsRate = hotel.lowestNightlyPoints !== undefined;
    return hotel.tags?.filter(
      (tag) => tag.stayTag === "Great Points Deal" && hasPointsRate
    );
  }, [hotel]);

  return (
    <div className={cn("relative w-full", className)} ref={ref}>
      {inView && onFavoriteToggle ? (
        <FavoriteButton
          isFavorite={favoriteState ?? false}
          pending={favoritePending || favoriteState === undefined}
          onToggle={onFavoriteToggle}
          tone={imageGalleryItems.length > 0 ? "image" : "surface"}
          className={cn(
            "absolute -top-1 z-10",
            closeCard ? "right-8" : "-right-1"
          )}
        />
      ) : null}
      {inView ? (
        <div
          className={cn(
            "relative flex w-full cursor-pointer select-none flex-col gap-3 rounded-lg",
            disableOnClick && "cursor-default",
            innerClassName
          )}
          role={navigationLabel ? "link" : undefined}
          tabIndex={navigationLabel && !disableOnClick ? 0 : undefined}
          aria-label={navigationLabel}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setHoveredHotelId?.(hotel.vervotechPropertyId)}
          onMouseLeave={() => setHoveredHotelId?.(undefined)}
          onClick={_onClick ? _onClick : onClick}
        >
          {isComparisonMode && hotel.vervotechPropertyId && (
            <Button
              variant="link"
              size="icon-sm"
              className={cn(
                "absolute left-2 top-2 z-10 h-7 w-7 border-2 transition-colors",
                isSelectedForComparison
                  ? "border-brand-gold bg-brand-gold text-brand-gold-foreground"
                  : "border-foreground/60 bg-pure-black/40 text-foreground/60 hover:border-foreground hover:text-foreground"
              )}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onComparisonToggle?.(hotel.vervotechPropertyId!);
              }}
              aria-label={
                isSelectedForComparison
                  ? "Remove from comparison"
                  : "Add to comparison"
              }
            >
              {isSelectedForComparison && (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </Button>
          )}
          {imageGalleryItems.length > 0 ? (
            <ImageGallery
              autoPlay={false}
              showPlayButton={false}
              showFullscreenButton={false}
              showThumbnails={false}
              showBullets={false}
              lazyLoad={true}
              items={imageGalleryItems}
              showNav={true}
              infinite={false}
              disableSwipe
              renderLeftNav={(onClickLeft, disabled) => {
                if (disabled) return null;
                return (
                  <ArrowCircleLeft
                    className="absolute left-1 top-1/2 z-[9] h-8 w-8 -translate-y-1/2 transform cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickLeft(e as unknown as MouseEvent<HTMLImageElement>);
                    }}
                  />
                );
              }}
              renderRightNav={(onClickRight, disabled) => {
                if (disabled) return null;
                return (
                  <ArrowCircleRight
                    className="absolute right-1 top-1/2 z-[9] h-8 w-8 -translate-y-1/2 transform cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickRight(
                        e as unknown as MouseEvent<HTMLImageElement>
                      );
                    }}
                  />
                );
              }}
            />
          ) : (
            <div
              className={cn(
                imageHeight,
                "flex items-center justify-center overflow-hidden rounded-lg bg-card"
              )}
            >
              <span className="text-primary">No Image Available</span>
            </div>
          )}
          {showDateRangeTag ? (
            <div
              className={cn(
                "absolute left-3",
                isComparisonMode ? "top-14" : "top-3"
              )}
            >
              <Badge className="rounded-full bg-pure-white text-pure-black shadow">
                <Calendar className="text-pure-black" />
                {format(getDateFormat(checkinStr), "MMM dd")} -{" "}
                {format(getDateFormat(checkoutStr), "MMM dd")}
              </Badge>
            </div>
          ) : showTags && hotelTags?.length ? (
            <div
              className={cn(
                "absolute left-3 flex flex-col items-start justify-start gap-1",
                isComparisonMode ? "top-14" : "top-3"
              )}
            >
              {hotelTags.map((tag) => (
                <HotelTag key={tag.stayTag} {...tag} />
              ))}
            </div>
          ) : null}
          {closeCard && (
            <div className="absolute right-1 top-1 flex items-center gap-2 p-2">
              <div
                className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-pure-white"
                onClick={(e) => {
                  e.stopPropagation();
                  closeCard();
                }}
              >
                <X className="h-4 w-4" />
              </div>
            </div>
          )}
          <div className={cn("flex w-full flex-col pt-0", !noPadding && "p-2")}>
            <div
              className={cn(
                "mb-4 flex flex-wrap items-center gap-x-2 gap-y-1",
                reserveLoyaltyTagSpace && "min-h-6"
              )}
            >
              {hotel.loyaltyProgram ? (
                <Badge
                  variant="loyalty"
                  className="text-pure-white"
                  style={{
                    backgroundColor:
                      loyaltyAccountsColorMapping[
                        hotel.loyaltyProgram as LoyaltyProgramEnum
                      ],
                  }}
                >
                  {hotel.loyaltyProgram}
                </Badge>
              ) : (
                <Badge variant="muted">
                  {hotel.chainName?.trim() || "Independent"}
                </Badge>
              )}
              {transferPartners.length ? (
                <>
                  <span className="inline-flex items-center gap-x-2">
                    <span className="inline-block h-5 min-w-px bg-border"></span>
                    <Badge variant="outline">{transferPartners[0]}</Badge>
                  </span>
                  {transferPartners.slice(1).map((transferPartner) => (
                    <Badge variant="outline" key={transferPartner}>
                      {transferPartner}
                    </Badge>
                  ))}
                </>
              ) : null}
              {freeNightMatch?.coverage === "full" &&
                freeNightMatch.programName === hotel.loyaltyProgram && (
                  <Badge
                    variant="outline"
                    className="border-primary/40 bg-primary/5 text-primary"
                    title={freeNightMatch.badge}
                  >
                    FNC covers this
                  </Badge>
                )}
            </div>
            <div className="flex w-full items-start justify-between gap-2.5 self-stretch">
              <div className="flex h-12 flex-col text-base font-medium text-primary">
                <span className="line-clamp-2">{hotel.propertyName}</span>
              </div>
              {hotel.averageGuestRating && (
                <div className="mt-0.5 flex flex-col justify-center gap-x-1 text-right text-sm font-normal text-primary">
                  <span className="flex items-center justify-end gap-1">
                    <Star className="h-3 w-3" color="#faff1a" />
                    {getRating(hotel.averageGuestRating)}
                  </span>
                </div>
              )}
            </div>
            {activeFilterRate?.roomName && (
              <div className="line-clamp-1 text-sm font-medium text-muted-foreground">
                {activeFilterRate.roomName}
                {activeFilterRate.areaSqFt
                  ? ` • ${activeFilterRate.areaSqFt.toLocaleString()} sq ft`
                  : ""}
              </div>
            )}
            <div className="line-clamp-1 text-sm font-medium text-muted-foreground">
              {`${hotel.cityName}${hotel.countryCode ? `, ${getCountryName(hotel.countryCode)}` : ""}  ${hotel?.stars ? `• ${hotel.stars}-star hotel` : ""}`}
            </div>
            {showDate && !showDateRangeTag && (
              <div className="my-1 flex items-center gap-2">
                <Calendar
                  color={"hsl(var(--muted-foreground))"}
                  className="h-5 w-5"
                />
                <span className="text-xs text-primary">
                  {format(getDateFormat(checkinStr), "MMM dd")} -{" "}
                  {format(getDateFormat(checkoutStr), "MMM dd")} ({nights} night
                  stay)
                </span>
              </div>
            )}
            {(() => {
              // Determine which rate to show based on roomType and isRefundable filters
              const nightlyRate = getDisplayNightlyRate(
                hotel,
                filters?.isRefundable,
                filters?.roomType,
                filters?.bedType
              );
              const totalRate = getDisplayTotalCost(
                hotel,
                filters?.isRefundable,
                filters?.roomType,
                filters?.bedType
              );
              const totalRateCurrency = hotel.lowestRateCurrency;
              // GON-6088: a bookable rate can have a total but no per-night value
              // (e.g. the backend's nightly-suppression guard on high-resort-fee
              // hotels). Keep it out of "sold out": show "---/night" with the real
              // total underneath rather than a fabricated nightly.
              return nightlyRate || totalRate || hotel.lowestNightlyPoints ? (
                <div className="flex w-full items-center gap-2 py-0.5">
                  {nightlyRate ? (
                    <div
                      className={cn(
                        "flex w-1/2 flex-1 items-end justify-between border-border",
                        !!hotel.lowestNightlyPoints && "border-r"
                      )}
                    >
                      <div className="flex w-full flex-col">
                        <div className="flex flex-col">
                          <div className="flex items-center justify-start gap-1">
                            <span className="line-clamp-1 text-base text-primary">
                              {formatCurrency(
                                nightlyRate,
                                totalRateCurrency,
                                0
                              )}
                              <span className="h-4 text-xs text-muted-foreground">
                                {" "}
                                /night
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="flex w-full flex-1 items-center justify-between">
                          {!!totalRate && (
                            <span className="text-xs text-muted-foreground">
                              {`${formatCurrency(totalRate, totalRateCurrency, 0)} total w/ tax & fees`}
                            </span>
                          )}

                          {sortBy === "cash_percentile" &&
                            hotel.cashDealScore && (
                              <Badge
                                className={cn(
                                  "me-3 py-0 text-pure-black",
                                  hotel.cashDealScore >= 7
                                    ? "bg-rate-quality-good"
                                    : hotel.cashDealScore >= 4
                                      ? "bg-rate-quality-default"
                                      : "bg-rate-quality-bad"
                                )}
                              >
                                {hotel.cashDealScore.toFixed(1)}
                              </Badge>
                            )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "flex w-1/2 flex-1 items-end justify-between border-border",
                        !!hotel.lowestNightlyPoints && "border-r"
                      )}
                    >
                      <div className="flex w-full flex-col">
                        <div className="flex flex-col">
                          <div className="flex items-center justify-start gap-1">
                            <span className="line-clamp-1 text-base text-primary">
                              ---
                              <span className="h-4 text-xs text-muted-foreground">
                                {" "}
                                /night
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="flex w-full flex-1 items-center justify-between">
                          {/* GON-6088: when only a total is available, show it
                              beneath the "---/night" placeholder instead of
                              "View Rates" — never a fabricated per-night value. */}
                          <span className="text-xs text-muted-foreground">
                            {totalRate
                              ? `${formatCurrency(totalRate, totalRateCurrency, 0)} total w/ tax & fees`
                              : "View Rates"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!!hotel.lowestNightlyPoints && (
                    <div className="flex w-1/2 flex-1 items-end justify-between">
                      <div className="flex w-full flex-col">
                        <div className="flex items-center justify-start gap-1">
                          <img
                            src="/assets/points-white.svg"
                            alt="rate"
                            className="h-4 w-4"
                          />
                          <span className="line-clamp-1 text-base text-primary">
                            {formatNumber(hotel.lowestNightlyPoints, 0)}
                            <span className="text-xs text-muted-foreground">
                              {" "}
                              pts / night
                            </span>
                          </span>
                        </div>

                        <div className="flex w-full flex-1 items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {formatNumber(hotel.lowestPoints!, 0)} pts total
                          </span>
                          {sortBy !== "cash_percentile" &&
                            hotel.lowestPointsAmount?.conversionRate && (
                              <Badge
                                className={cn(
                                  "ms-auto py-0 text-pure-black",
                                  approximatePercentile === "high"
                                    ? "bg-rate-quality-good"
                                    : "bg-rate-quality-default"
                                )}
                              >
                                {hotel.lowestPointsAmount?.conversionRate} ¢/pt
                              </Badge>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : noRateAffordance === "loading" ? (
                // GON-6020 content-first: rates haven't streamed in for this
                // card yet — show a loading affordance instead of "sold out".
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <Spinner size="xs" />
                  Checking rates…
                </div>
              ) : noRateAffordance === "unavailable" ? (
                // Search finished and this property still has no rate.
                <div className="mt-4 flex text-sm text-muted-foreground">
                  No rates available
                </div>
              ) : noRateAffordance === "view-rates" ? (
                <div className="mt-4 flex text-sm text-muted-foreground">
                  View rates
                </div>
              ) : (
                <div className="mt-4 flex text-sm text-destructive">
                  We are sold out for these dates
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <HotelCardSkeleton />
      )}
    </div>
  );
};

const HotelTag: React.FC<Tag> = ({ stayTag, tagReason }) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  return (
    <Tooltip delayDuration={0} open={tooltipOpen}>
      <TooltipTrigger
        asChild
        onClick={(e) => {
          e.stopPropagation();
          setTooltipOpen((prev) => !prev);
        }}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <Badge className="bg-pure-white text-pure-black shadow">
          {stayTag}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px] lg:max-w-[400px]">
        {tagReason}
      </TooltipContent>
    </Tooltip>
  );
};

export default HotelCard;
